import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Work } from "@/types/work";
import { TYPE_LABELS, formatHeat } from "@/lib/works";

interface HeroCarouselProps {
  slides: Work[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = slides.length;

  const active = useMemo(() => slides[index], [slides, index]);

  useEffect(() => {
    if (paused || count <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % count);
    }, 7000);
    return () => window.clearInterval(timer);
  }, [paused, count]);

  if (!active) return null;

  return (
    <section
      className="relative w-full overflow-hidden bg-background-100"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点作品轮播"
    >
      <div className="relative h-[620px] md:h-[720px] w-full">
        {slides.map((work, slideIndex) => (
          <div
            key={work.id}
            className={`absolute inset-0 transition-opacity duration-[900ms] ease-out ${
              slideIndex === index ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={slideIndex !== index}
          >
            <img
              src={work.backdrop ?? work.poster}
              alt={`${work.title} 剧照`}
              title={`${work.title} 剧情介绍`}
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 hero-mask" />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/10 to-background-50/55" />
          </div>
        ))}

        <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-center">
          <div className="w-full max-w-[1400px] mx-auto pt-16">
            <div key={active.id} className="max-w-2xl animate-fade-up">
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500/15 border border-primary-500/40 text-primary-300 text-xs tracking-wide">
                  <i className="ri-fire-line text-accent-400" />
                  本周焦点
                </span>
                <span className="text-xs text-foreground-600 tracking-[0.2em] font-label uppercase">
                  {TYPE_LABELS[active.type]} · {active.year} · {active.region}
                </span>
              </div>

              <h2 className="mt-5 font-heading text-4xl md:text-6xl font-semibold leading-[1.08] text-foreground-950">
                {active.title}
              </h2>

              <p className="mt-4 text-base md:text-lg text-foreground-700 leading-relaxed max-w-xl">{active.tagline}</p>

              <p className="mt-4 text-sm leading-relaxed text-foreground-600 max-w-xl line-clamp-3 md:line-clamp-none">
                {active.synopsis}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-600">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-400" />
                  <span className="font-label text-foreground-900 text-sm">{active.rating.toFixed(1)}</span>
                  <span>/ 10</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-user-star-line" />
                  {formatHeat(active.heat)} 人关注
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-time-line" />
                  {active.duration}
                </span>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Link
                  to={`/detail/${active.id}`}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line text-lg" />
                  查看剧情详情
                </Link>
                <Link
                  to={`/search?q=${encodeURIComponent(active.genres[0] ?? "")}`}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-background-300/70 bg-background-50/50 backdrop-blur text-foreground-800 hover:border-primary-500/70 hover:text-foreground-950 text-sm transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-compass-3-line text-lg" />
                  浏览同类作品
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 inset-x-0 z-20 px-4 md:px-8">
          <div className="w-full max-w-[1400px] mx-auto flex items-end justify-between gap-6">
            <div className="flex items-center gap-2">
              {slides.map((work, slideIndex) => (
                <button
                  key={work.id}
                  type="button"
                  onClick={() => setIndex(slideIndex)}
                  aria-label={`切换到 ${work.title}`}
                  className={`h-[3px] rounded-full transition-all duration-300 cursor-pointer ${
                    slideIndex === index ? "w-10 bg-primary-500" : "w-5 bg-background-400/70 hover:bg-background-500"
                  }`}
                />
              ))}
              <span className="ml-3 font-label text-xs tracking-[0.2em] text-foreground-600">
                {String(index + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
              </span>
            </div>

            <div className="hidden sm:flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIndex((index - 1 + count) % count)}
                className="w-10 h-10 rounded-full border border-background-300/70 bg-background-50/50 backdrop-blur text-foreground-800 flex items-center justify-center hover:border-primary-500 hover:text-foreground-950 transition-colors cursor-pointer"
                aria-label="上一张"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <button
                type="button"
                onClick={() => setIndex((index + 1) % count)}
                className="w-10 h-10 rounded-full border border-background-300/70 bg-background-50/50 backdrop-blur text-foreground-800 flex items-center justify-center hover:border-primary-500 hover:text-foreground-950 transition-colors cursor-pointer"
                aria-label="下一张"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}